import { Component, Input, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  FormControl,
  ValidationErrors
} from '@angular/forms';

import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import defaultLanguageModel from '@zxcvbn-ts/language-en';

@Component({
  selector: 'app-password-field',
  templateUrl: './password-field.component.html',
  styleUrls: ['./password-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true
    }
  ]
})
export class PasswordFieldComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() label = 'Password';
  @Input() placeholder = '';
  @Input() inputId = 'password';
  @Input() confirm = false;
  @Input() minStrength = 3;

  @Input() confirmLabel = 'Confirm Password';
  @Input() confirmPlaceholder = 'Confirm password';

  @Input() containerClass = '';
  @Input() labelClass = '';
  @Input() inputClass = '';
  @Input() toggleButtonClass = '';
  @Input() confirmInputClass = '';
  @Input() confirmToggleButtonClass = '';
  @Input() strengthBarClass = '';
  @Input() suggestionsClass = '';

  value = '';
  confirmValue = '';

  touched = false;
  confirmTouched = false;

  showPassword = false;
  showConfirm = false;

  strength = 0;
  strengthLabel = 'Weak';
  strengthClass = 'text-danger';
  strengthPercent = 10;

  criteria = {
    minLength: false,
    upperLower: false,
    number: false,
    special: false
  };

  get passwordValue(): string {
    return this.value;
  }

  ngOnInit(): void {
    const { dictionary, translations } = defaultLanguageModel;
    zxcvbnOptions.setOptions({ dictionary, translations });
  }

  writeValue(val: string): void {
    this.value = val || '';
    this.evaluateStrength(this.value);
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setValue(val: string): void {
    this.value = val;
    this.onChange(val); // updates parent form
    this.markMainTouched();
    this.evaluateStrength(val);
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirm(): void {
    this.showConfirm = !this.showConfirm;
  }

  markMainTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  markConfirmTouched(): void {
    this.confirmTouched = true;
    this.onChange(this.value); // force re-validation
    this.onTouched();
  }

  evaluateStrength(password: string): void {
    const result = zxcvbn(password);
    this.strength = result.score;

    const scores = ['Weak', 'Weak', 'Moderate', 'Good', 'Strong'];
    const classes = ['text-danger', 'text-danger', 'text-warning', 'text-info', 'text-success'];
    const percents = [10, 25, 50, 75, 100];

    this.strengthLabel = scores[result.score];
    this.strengthClass = classes[result.score];
    this.strengthPercent = percents[result.score];

    this.criteria.minLength = password.length >= 8;
    this.criteria.upperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
    this.criteria.number = /\d/.test(password);
    this.criteria.special = /[^\w\s]/.test(password);
  }

  validate(_: FormControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    const hasMinLength = this.value.length >= 8;
    const hasUpperLower = /[a-z]/.test(this.value) && /[A-Z]/.test(this.value);
    const hasNumber = /\d/.test(this.value);
    const hasSpecial = /[^\w\s]/.test(this.value);

    if (!hasMinLength) {
      errors['minLength'] = true;
    }

    if (!hasUpperLower) {
      errors['upperLower'] = true;
    }

    if (!hasNumber) {
      errors['number'] = true;
    }

    if (!hasSpecial) {
      errors['special'] = true;
    }

    if (this.confirm && this.value !== this.confirmValue) {
      errors['passwordMismatch'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};
}
